import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, ErrorMessage } from 'formik';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import useUtils from 'hooks/useUtils';
import { unstable_HistoryRouter } from 'react-router-dom';
import { format } from 'date-fns';

export default function JournalEntryForm() {
    const [entryDate, setEntryDate] = useState(new Date());
    const [description, setDescription] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [rows, setRows] = useState([]);
    const toast = useRef();
    const utils = useUtils();
    const [reference_no, setRef] = useState(() => utils.randomNum(10));

    useEffect(() => {
         const apiUrl2 = `${process.env.REACT_APP_API_PATH}Components_Data/ac_code_option_list`;
        fetch (apiUrl2)
            .then(res => res.json())
            .then(data => setAccounts(data));
    }, []);

    const addRow = () => {
        setRows([...rows, { accountID: null, debit: 0, credit: 0 }]);
    };

    const deleteRow = (index) => {
        const newRows = [...rows];
        newRows.splice(index, 1);
        setRows(newRows);
    };

    const updateRow = (index, field, value) => {
        const updated = [...rows];
        updated[index][field] = value;
        setRows(updated);
    };

const totalDebit = rows.reduce((s, r) => s + (r.debit || 0), 0);
const totalCredit = rows.reduce((s, r) => s + (r.credit || 0), 0);

const saveEntry = async () => {

       if (!entryDate) {
        toast.current.show({
            severity: 'error',
            summary: 'Validation Error',
            detail: 'Date is required'
        });
        return;
    }

    // REQUIRED: Description
    if (!description || description.trim() === "") {
        toast.current.show({
            severity: 'error',
            summary: 'Validation Error',
            detail: 'Description is required'
        });
        return;
    }

   if (!String(reference_no).trim()) {
    toast.current.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Reference number is required'
    });
    return;
}

    if (totalDebit !== totalCredit) {
        toast.current.show({
            severity: 'error',
            summary: 'Validation Error',
            detail: 'Total Debit must equal Total Credit'
        });
        return;
    }

    if (rows.length === 0) {
        toast.current.show({
            severity: 'warn',
            summary: 'No Ledger Rows',
            detail: 'Add at least one row'
        });
        return;
    }
    
    const formattedDateFrom = format(new Date(entryDate), 'yyyy-MM-dd');
 
    // ---- Correct Payload  entryDate.toISOString().slice(0, 10)----
  const payload = {
    journalm: {
        transactiondate:formattedDateFrom,
        narration: description,
        voucher_ref_no: reference_no
    },
    ledger: rows.map(r => ({
        ac_code: r.accountid,
        debit: Number(r.debit || 0),
        credit: Number(r.credit || 0)
    }))
};


    try {
        const res = await fetch(`${process.env.REACT_APP_API_PATH}journal/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!res.ok) {
            const errorText = await res.text();
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: errorText
            });
            return;
        }

        toast.current.show({
            severity: 'success',
            summary: 'Saved',
            detail: 'Journal Entry Saved Successfully'
        });

        // Reset form

        setRows([]);
        setDescription('');
        setRef(utils.randomNum(10));
        setEntryDate(new Date());
     

    } catch (error) {
        toast.current.show({
            severity: 'error',
            summary: 'Error',
            detail: error.message
        });
    }
};


 const accountTemplate = (rowData, options) => (
    <Dropdown
        value={rowData.accountid}       // 🔥 FIX
        options={accounts}
        optionLabel="label"
        optionValue="value"
        placeholder="Select Account"
        className="w-full"
        filter
        showFilterClear
        onChange={(e) => updateRow(options.rowIndex, 'accountid', e.value)}  // 🔥 FIX
    />
);

    const debitTemplate = (rowData, options) => (
        <InputNumber
            value={rowData.debit}
            min={0}
            mode="decimal"
            onChange={(e) => updateRow(options.rowIndex, 'debit', e.value)}
            showButtons
            className="w-full"
        />
    );

    const creditTemplate = (rowData, options) => (
        <InputNumber
            value={rowData.credit}
            min={0}
            mode="decimal"
            onChange={(e) => updateRow(options.rowIndex, 'credit', e.value)}
            showButtons
            className="w-full"
        />
    );

    const deleteTemplate = (_, options) => (
        <Button icon="pi pi-trash" className="p-button-danger" onClick={() => deleteRow(options.rowIndex)} />
    );

    return (
        <div className="p-4 card">
            <Toast ref={toast} />

            <h2>Journal Entry</h2>

            <div className="grid formgrid p-fluid">
                <div className="col-12 md:col-4">
                    <label>Date</label>
                    <Calendar value={entryDate} onChange={(e) => setEntryDate(e.value)} dateFormat="dd-mm-yy" className="w-full" />
                </div>
                <div className="col-12 md:col-4">
                    <label>Description</label>
                    <InputText value={description} onChange={(e) => setDescription(e.target.value)} className="w-full" />
                </div>
                   <div className="col-12 md:col-4">
                    <label>Reference No</label>
                    <InputText value={reference_no} onChange={(e) => setRef(e.target.value)} className="w-full" />
                </div>

            </div>

            <h3>Ledger Details</h3>

            <Button label="Add Row" icon="pi pi-plus" onClick={addRow} className="mb-3" />

            <DataTable value={rows} className="p-datatable-sm">
                <Column header="Account" body={accountTemplate} />
                <Column header="Debit" body={debitTemplate} />
                <Column header="Credit" body={creditTemplate} />
                <Column header="Delete" body={deleteTemplate} />
            </DataTable>

            <div className="mt-3 text-right">
                <h4>Total Debit: ₹ {totalDebit.toFixed(2)} | Total Credit: ₹ {totalCredit.toFixed(2)}</h4>
            </div>

            <Button label="Save Journal Entry" icon="pi pi-check" className="mt-3" onClick={saveEntry} />
        </div>
    );
}

